import { useContext } from 'react';

import { Button, Text, VStack } from '@chakra-ui/react';
import { MdOutlineLockClock } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useUnlockDealMutation } from '../../gql/generated/graphql';

import CustomerContact from '../../components/CustomerContact';
import Modal from '../../components/shared/Modal';

import { DealContext } from '../../libs/DealContext';

type DealLockedModalProps = {
  isOpen: boolean;
  expirationDate?: Date | string;
};

const DealLockedModal = ({ isOpen, expirationDate }: DealLockedModalProps) => {
  const { deal } = useContext(DealContext);
  const history = useHistory();

  const formattedExpirationDate =
    typeof expirationDate === 'string'
      ? new Date(expirationDate).toLocaleString()
      : expirationDate?.toLocaleString();

  const [unlockDealMutation, { loading: loadingUnlockDeal }] = useUnlockDealMutation();

  const handleUnlock = async () => {
    if (!deal.id) {
      toast.error('Deal not found.');
      return;
    }

    try {
      const { data } = await unlockDealMutation({ variables: { dealId: deal.id } });
      if (!data?.unlockDeal) {
        throw new Error();
      }

      toast.success('Deal unlocked and link invalidated.');
      // The `secure_link` field is updated via a subscription.
    } catch (e) {
      toast.error('Failed to unlock deal.');
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={history.goBack}
      canDismiss={false}
      size="xl"
      variant="noHeader"
      trapFocus={false} // Needed to be able to interact with the text widget.
      leftButtons={
        <Button onClick={history.goBack} variant="secondary">
          BACK
        </Button>
      }
      rightButtons={
        <>
          <CustomerContact inDealLockedModal mr={4} />

          <Button onClick={handleUnlock} variant="boot" isLoading={loadingUnlockDeal}>
            UNLOCK
          </Button>
        </>
      }
    >
      <VStack spacing={6} align="center" mt={4} mb={2}>
        <MdOutlineLockClock size={50} />
        <VStack spacing={2} align="start">
          <Text>Deal is locked for customer entry until {formattedExpirationDate}.</Text>
          <Text>Unlocking deal will invalidate customer link.</Text>
        </VStack>
      </VStack>
    </Modal>
  );
};

export default DealLockedModal;
